import { Shield, AlertTriangle, Home, ArrowLeft } from "lucide-react";
import { Card, CardContent } from "@/components/atoms/card";
import { useNavigate } from "react-router-dom";
import { Button } from "../atoms/button";

interface AccessDeniedProps {
  title?: string;
  message?: string;
  showHomeButton?: boolean;
  showBackButton?: boolean;
  variant?: "page" | "inline" | "modal";
}

export function AccessDenied({
  title = "Acceso Denegado",
  message = "No tienes permisos para acceder a esta sección. Contacta a tu administrador si crees que esto es un error.",
  showHomeButton = true,
  showBackButton = true,
  variant = "page",
}: AccessDeniedProps) {
  const navigate = useNavigate();

  const handleGoHome = () => {
    navigate("/");
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  const content = (
    <>
      <div className="text-center my-6">
        <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
          <Shield className="w-8 h-8 text-red-600" />
        </div>
        <h2
          className={`font-semibold text-gray-900 mb-2 ${variant === "page" ? "text-2xl" : "text-lg"}`}
        >
          {title}
        </h2>
        <p className="text-gray-600 max-w-md mx-auto">{message}</p>
      </div>

      {(showHomeButton || showBackButton) && (
        <div className="flex gap-3 justify-center">
          {showBackButton && (
            <Button
              variant="outline"
              onClick={handleGoBack}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Volver
            </Button>
          )}
          {showHomeButton && (
            <Button onClick={handleGoHome} className="flex items-center gap-2">
              <Home className="w-4 h-4" />
              Ir al inicio
            </Button>
          )}
        </div>
      )}
    </>
  );

  if (variant === "page") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md w-full">
          <Card>
            <CardContent className="pt-6">{content}</CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (variant === "modal") {
    return <div className="p-6">{content}</div>;
  }

  // inline variant
  return (
    <Card className="max-w-md mx-auto">
      <CardContent className="pt-6">{content}</CardContent>
    </Card>
  );
}

// Specific access denied components for common scenarios
export function BusinessAccessDenied() {
  return (
    <AccessDenied
      title="Negocio no autorizado"
      message="No tienes permisos para acceder a este negocio. Contacta a tu administrador para solicitar acceso."
    />
  );
}

export function BranchAccessDenied() {
  return (
    <AccessDenied
      title="Sucursal no autorizada"
      message="No tienes permisos para acceder a esta sucursal. Contacta a tu administrador para solicitar acceso."
    />
  );
}

export function InsufficientPermissions() {
  return (
    <AccessDenied
      title="Permisos insuficientes"
      message="Tu rol actual no tiene los permisos necesarios para realizar esta acción."
      variant="inline"
    />
  );
}

// Simple warning component for smaller UI elements
export function PermissionWarning({
  message = "Acción restringida",
  size = "sm",
}: {
  message?: string;
  size?: "sm" | "md" | "lg";
}) {
  const iconSize =
    size === "sm" ? "w-4 h-4" : size === "md" ? "w-5 h-5" : "w-6 h-6";
  const textSize =
    size === "sm" ? "text-xs" : size === "md" ? "text-sm" : "text-base";

  return (
    <div className="flex items-center gap-2 text-amber-600 bg-amber-50 p-2 rounded-md border border-amber-200">
      <AlertTriangle className={`${iconSize} flex-shrink-0`} />
      <span className={`${textSize} font-medium`}>{message}</span>
    </div>
  );
}
